import { BaseSettingsEndpoints } from './base';
import {
  WorkplaceRequestCommentsResponse,
  WorkplaceRequestCommentsParams,
  CreateWorkplaceRequestCommentParams,
  UpdateWorkplaceRequestCommentParams,
  WorkplaceRequestComment,
  WorkplaceRequestCommentAttachmentInput
} from '../types/workplace-request-comments';

const WORKPLACE_REQUEST_COMMENTS_BASE_PATH = '/v1/tickets';

/**
 * Body of a comment write. Both members are optional on the wire: the backend
 * requires text or at least one attachment, and on update an omitted
 * `attachments` means "keep the current ones".
 */
type CommentWriteBody = {
  attachments?: WorkplaceRequestCommentAttachmentInput[];
  comment?: string;
  /** Create only: the backend has no way to change it afterwards. */
  internal?: boolean;
};

const buildCommentBody = (
  comment: string | undefined,
  attachments: WorkplaceRequestCommentAttachmentInput[] | undefined
): CommentWriteBody => {
  const body: CommentWriteBody = {};
  if (comment !== undefined) {
    body.comment = comment;
  }
  if (attachments !== undefined) {
    body.attachments = attachments;
  }
  return body;
};

export class WorkplaceRequestCommentsClient extends BaseSettingsEndpoints {
  async getWorkplaceRequestComments(
    ticketId: string,
    params: WorkplaceRequestCommentsParams = {}
  ) {
    const { cursor, limit, tz = 'UTC' } = params;

    const queryParams = new URLSearchParams();
    // queryParams.set('tz', tz);

    if (cursor) {
      queryParams.set('cursor', cursor);
    }

    if (limit) {
      queryParams.set('limit', limit.toString());
    }

    const queryString = queryParams.toString();
    const url = `${WORKPLACE_REQUEST_COMMENTS_BASE_PATH}/${ticketId}/comments${queryString ? `?${queryString}` : ''}`;

    return this.get<WorkplaceRequestCommentsResponse>(url);
  }

  async createWorkplaceRequestComment(
    ticketId: string,
    params: CreateWorkplaceRequestCommentParams
  ) {
    const { comment, attachments, internal, tz = 'UTC' } = params;

    const queryParams = new URLSearchParams();
    // queryParams.set('tz', tz);

    const queryString = queryParams.toString();
    const url = `${WORKPLACE_REQUEST_COMMENTS_BASE_PATH}/${ticketId}/comments${queryString ? `?${queryString}` : ''}`;

    const body = buildCommentBody(comment, attachments);
    if (internal) {
      body.internal = true;
    }

    return this.post<CommentWriteBody, WorkplaceRequestComment>(url, body);
  }

  async updateWorkplaceRequestComment(
    ticketId: string,
    commentId: string,
    params: UpdateWorkplaceRequestCommentParams
  ) {
    const { comment, attachments, tz = 'UTC' } = params;

    const queryParams = new URLSearchParams();
    // queryParams.set('tz', tz);

    const queryString = queryParams.toString();
    const url = `${WORKPLACE_REQUEST_COMMENTS_BASE_PATH}/${ticketId}/comments/${commentId}${queryString ? `?${queryString}` : ''}`;

    return this.update<CommentWriteBody, WorkplaceRequestComment>(
      url,
      buildCommentBody(comment, attachments)
    );
  }

  async deleteWorkplaceRequestComment(
    ticketId: string,
    commentId: string
  ) {
    const url = `${WORKPLACE_REQUEST_COMMENTS_BASE_PATH}/${ticketId}/comments/${commentId}`;

    return this.delete(url);
  }
}
